import type { DayOfWeekEnum } from "~/__generated__/CalendarPageCalendarsQuery.graphql";
import type { frequency as FrequencyChoice } from "~/__generated__/useSaveEventMutationMutation.graphql";

export function formatTime(lengthInMinutes: number): string {
	if (lengthInMinutes < 60) {
		return `${lengthInMinutes} minutes`;
	} else {
		const hours = Math.floor(lengthInMinutes / 60);
		const minutes = lengthInMinutes % 60;
		const hoursText = hours > 1 ? "hrs" : "hr";
		let minutesText = "";

		if (minutes !== 0) {
			minutesText = `${`0${minutes}`.slice(-2)} mins`;
		}

		return `${hours} ${hoursText} ${minutesText}`;
	}
}

export function calculateMeetingCost(
	numAttendees: number,
	meetingLength: number,
	numOccurrences: number,
	meetingCostUnit?: string,
	meetingCostAvgSalary?: number,
): { meetingCostOccurrence: string; meetingCostSeries: string; isMonetary: boolean } {
	if (meetingCostUnit === "currency" && meetingCostAvgSalary) {
		const occurrenceCostValue = (meetingCostAvgSalary / 52 / 40) * numAttendees * (meetingLength / 60);
		const seriesCostValue = occurrenceCostValue * numOccurrences;

		const monetaryCostOccurrence = getLocaleCurrency(occurrenceCostValue);
		const monetaryCostSeries = getLocaleCurrency(seriesCostValue);

		return { meetingCostOccurrence: monetaryCostOccurrence, meetingCostSeries: monetaryCostSeries, isMonetary: true };
	}

	const meetingCostInMinutesOccurrence = numAttendees * meetingLength;
	const meetingCostInMinutesSeries = meetingCostInMinutesOccurrence * numOccurrences;
	return {
		meetingCostOccurrence: formatTime(meetingCostInMinutesOccurrence),
		meetingCostSeries: formatTime(meetingCostInMinutesSeries),
		isMonetary: false,
	};
}

export function calculateSpeedyMeetingDates(
	isPromptEnabled: boolean | undefined,
	promptBehaviour: string | undefined,
	startDate: Date,
	endDate?: Date,
): { newStartDate: Date; newEndDate: Date } {
	let newStartDate = startDate;
	// if no endDate is provided, default to 30 minutes after the startDate
	let newEndDate = endDate ?? new Date(newStartDate.getTime() + 30 * 60000);

	if (isPromptEnabled) {
		const meetingLengthInMinutes = (newEndDate.getTime() - newStartDate.getTime()) / 60000;
		const minutesToChange = meetingLengthInMinutes >= 60 ? 10 : meetingLengthInMinutes > 15 ? 5 : 0;

		if (promptBehaviour === "START_LATE") {
			newStartDate.setMinutes(newStartDate.getMinutes() + minutesToChange);
		} else if (promptBehaviour === "END_EARLY") {
			newEndDate.setMinutes(newEndDate.getMinutes() - minutesToChange);
		}
	}

	return { newStartDate, newEndDate };
}

export function calculateDateToAvoidShortNoticeMeeting(
	isPromptEnabled: boolean | undefined,
	hoursInAdvanceThreshold: number | undefined,
	startDate: Date,
	endDate: Date,
): { newStartDate: Date; newEndDate: Date } {
	let newStartDate = startDate;
	let newEndDate = endDate;

	if (isPromptEnabled && hoursInAdvanceThreshold) {
		const hoursInAdvanceThresholdInMilliseconds = hoursInAdvanceThreshold * 3600000;
		const currentDate = new Date();
		const timeDifference = newStartDate.getTime() - currentDate.getTime();

		if (timeDifference < hoursInAdvanceThresholdInMilliseconds) {
			newStartDate = new Date(startDate.getTime() + hoursInAdvanceThresholdInMilliseconds);
			newEndDate = new Date(newStartDate.getTime() + (endDate.getTime() - startDate.getTime()));
		}
	}

	return { newStartDate, newEndDate };
}

export function calculateDateToAvoidNoMeetingDay(
	isPromptEnabled: boolean | undefined,
	noMeetingDay: DayOfWeekEnum | undefined,
	startDate: Date,
	endDate: Date,
): { newStartDate: Date; newEndDate: Date } {
	const noMeetingDay2Int = {
		SUNDAY: 0,
		MONDAY: 1,
		TUESDAY: 2,
		WEDNESDAY: 3,
		THURSDAY: 4,
		FRIDAY: 5,
		SATURDAY: 6,
	};

	let newStartDate = startDate;
	let newEndDate = endDate;

	if (isPromptEnabled && noMeetingDay) {
		const currentDate = new Date();
		const currentDay = currentDate.getUTCDay();
		const isNoMeetingDay = noMeetingDay2Int[noMeetingDay] === currentDay;

		if (isNoMeetingDay) {
			// Move meeting slot to 24 hours later
			newStartDate = new Date(startDate.getTime() + 86400000);
			newEndDate = new Date(newStartDate.getTime() + (endDate.getTime() - startDate.getTime()));
		}
	}

	return { newStartDate, newEndDate };
}

export function getLocaleCurrency(cost: number): string {
	const locale = Intl.NumberFormat().resolvedOptions().locale;

	return new Intl.NumberFormat(locale, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
		.format(cost)
		.replace("US", "");
}

function getFreqIntervalDaysFromRRule(RRule: string) {
	let RRuleFreq: FrequencyChoice = "DAILY";
	let RRuleInterval = 1;
	let RRuleRecurrenceDays: string[] = [];

	const RRuleFreqMatch = RRule.match(/FREQ=(\w+)/);
	const RRuleIntervalMatch = RRule.match(/INTERVAL=(\d+)/);
	const RRuleRecurrenceDaysMatch = RRule.match(/BYDAY=([A-Z,]+)/);

	if (RRuleFreqMatch) {
		RRuleFreq = RRuleFreqMatch[1] as FrequencyChoice;
	}

	if (RRuleIntervalMatch) {
		RRuleInterval = parseInt(RRuleIntervalMatch[1]);
	}

	if (RRuleRecurrenceDaysMatch) {
		RRuleRecurrenceDays = RRuleRecurrenceDaysMatch[1].split(",");
	}

	return { RRuleFreq, RRuleInterval, RRuleRecurrenceDays };
}

export function getNumOccurrencesFromRRule(
	RRule: string | null | undefined,
	eventStartDate: Date,
): {
	numOccurrences: number;
	isInfinite: boolean;
} {
	if (RRule == null) {
		return { numOccurrences: 1, isInfinite: false };
	}

	const countMatch = RRule.match(/COUNT=(\d+)/);
	if (countMatch) {
		return { numOccurrences: parseInt(countMatch[1]), isInfinite: false };
	}

	const untilMatch = RRule.match(/UNTIL=(\d{8})/);
	if (untilMatch) {
		const date = untilMatch[1];
		const year = parseInt(date.slice(0, 4));
		const month = parseInt(date.slice(4, 6));
		const day = parseInt(date.slice(6, 8));

		const untilDate = new Date(year, month - 1, day);

		const resetEventStartDate = new Date(eventStartDate);
		resetEventStartDate.setHours(0, 0, 0, 0);

		const numDaysDiff = (untilDate.getTime() - resetEventStartDate.getTime()) / (1000 * 3600 * 24);

		const { RRuleFreq, RRuleInterval, RRuleRecurrenceDays } = getFreqIntervalDaysFromRRule(RRule);

		if (RRuleFreq === "DAILY") {
			return { numOccurrences: numDaysDiff / RRuleInterval, isInfinite: false };
		} else if (RRuleFreq === "WEEKLY") {
			return {
				numOccurrences: (Math.floor(numDaysDiff / 7 / RRuleInterval) + 1) * RRuleRecurrenceDays.length,
				isInfinite: false,
			};
		} else if (RRuleFreq === "MONTHLY") {
			return { numOccurrences: Math.floor(numDaysDiff / 30 / RRuleInterval) + 1, isInfinite: false };
		}

		return { numOccurrences: 1, isInfinite: false };
	}

	return { numOccurrences: getNumOccurrencesForYear(RRule), isInfinite: true };
}

function getNumOccurrencesForYear(RRule: string): number {
	const FREQ_TO_DAYS = {
		DAILY: 365,
		WEEKLY: 52,
		MONTHLY: 12,
		YEARLY: 1,
	};

	const { RRuleFreq, RRuleInterval, RRuleRecurrenceDays } = getFreqIntervalDaysFromRRule(RRule);

	if (RRuleFreq === "DAILY") {
		return FREQ_TO_DAYS[RRuleFreq] / RRuleInterval;
	} else if (RRuleFreq === "WEEKLY") {
		return (FREQ_TO_DAYS[RRuleFreq] / RRuleInterval) * RRuleRecurrenceDays.length;
	} else if (RRuleFreq === "MONTHLY") {
		return FREQ_TO_DAYS[RRuleFreq] / RRuleInterval;
	}

	return 1;
}
